import React, { createContext, useState, useEffect } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useQueryClient } from "@tanstack/react-query";
import refreshAccessToken from "../requireAuth/persistLogIn";
import { axiosWithCredentials } from "../api/axios";
import { jwtDecode } from "jwt-decode";

const AppContext = createContext();

export const AuthProvider = ({ children }) => {
  // const [auth, setAuth] = useState({});
  const [auth, setAuth] = useState({
    accessToken: null,
    isProfileComplete: null, // Track configuration state securely here
    hasPendingPrepaidOrder: null,
    hasUnacknowledgedRedemption: null,

    // ── NEW FIELDS ──
    email: null,
    phoneNumber: null,
    isGuest: false,
    // hasPlacedOrder: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  // 🔒 UPDATE: We track login status by the existence of an active accessToken in memory
  const userToken = auth?.accessToken || null;
  const isProfileComplete = auth?.isProfileComplete ?? null;
  const hasUnacknowledgedRedemption = auth?.hasUnacknowledgedRedemption ?? null;

  console.log("userTokenCtx", userToken);
  console.log("isProfileCompleteCtx", isProfileComplete);
  console.log("hasUnacknowledgedRedemptionxx", hasUnacknowledgedRedemption);

  const [googleOnboardData, setGoogleOnboardData] = useState(null);

  const [userInfo, setUserInfo] = useState(null);
  const [firstname, setFirstName] = useState(null);
  const [isGuest, setIsGuest] = useState(null);
  const [activitySettings, setActivitySettings] = useState(null);

  const [isReverified, setIsReverified] = useState(false);
  const [lastInteractionTimestamp, setLastInteractionTimestamp] = useState(null);

  const queryClient = useQueryClient();

  //console.log("🏠 AuthProvider render — User Session Status Active:", !!userToken);

  // Timer logic for session re-verification
  useEffect(() => {
    const timer = setInterval(() => {
      if (lastInteractionTimestamp) {
        const now = new Date().getTime();
        const elapsed = now - lastInteractionTimestamp;
        const reverificationTimeLimit = 2 * 60 * 1000;

        if (elapsed > reverificationTimeLimit) {
          setIsReverified(false);
          setLastInteractionTimestamp(null);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [lastInteractionTimestamp]);

  const reverifyUser = () => {
    setIsReverified(true);
    setLastInteractionTimestamp(new Date().getTime());
  };

  const updateInteraction = () => {
    setLastInteractionTimestamp(new Date().getTime());
  };

  // 🔒 UPDATE: Clean, browser-compliant silent-refresh session validator
  const isLoggedIn = async () => {
    try {
      setIsLoading(true);
      //console.log("Checking for active browser session cookie via silent refresh...");
      //console.log("Auth", auth);

      // Hit our refresh endpoint. Browser natively passes the HttpOnly cookie.
      const refreshed = await refreshAccessToken(setAuth, setActivitySettings);

      console.log("isRefreshes", refreshed);

      if (refreshed && refreshed?.accessToken) {
        // 2. Decode the token right here in memory!
        const decoded = jwtDecode(refreshed.accessToken);
        const userInfoToken = decoded?.UserInfo;

        console.log("Decoded Session Metadata Context Payload:", userInfoToken);

        // 3. RELOAD RECOVERY STRATEGY:
        if (userInfoToken) {
          if (userInfoToken.isProfileComplete === false) {
            console.log("Incomplete Profile found on reload. Restoring input form maps...");
            setGoogleOnboardData({
              username: userInfoToken.username || "guest_",
              firstname: userInfoToken.firstname || "",
              lastname: userInfoToken.lastname || "",
              email: userInfoToken.email || "",
              profilePicture: userInfoToken.image || "",
              isGoogleAuth: true,
            });
          } else if (String(userInfoToken.isProfileComplete) === "true") {
            // ADD THIS: If it is true on recovery, force-feed the auth state right away!
            setAuth({
              accessToken: refreshed.accessToken,
              isProfileComplete: true,
              hasPendingPrepaidOrder: userInfoToken?.hasPendingPrepaidOrder,
              pendingOrderDetails: refreshed?.pendingOrderDetails,
              hasUnacknowledgedRedemption: refreshed?.hasUnacknowledgedRedemption,

              // Extract fields from JWT payload or refreshed response
              email: userInfoToken?.email || refreshed?.email || null,
              phoneNumber: userInfoToken?.phoneNumber || refreshed?.phoneNumber || null,
              isGuest: userInfoToken?.isGuest ?? refreshed?.isGuest ?? false,
              // hasPlacedOrder: userInfoToken?.hasPlacedOrder ?? refreshed?.hasPlacedOrder ?? false,
            });
          }
        }
      }

      if (!refreshed || !refreshed?.accessToken) {
        //console.log("No valid session cookie detected. User is unauthenticated.");
        setAuth({
          accessToken: null,
          isProfileComplete: null,
          hasPendingPrepaidOrder: null,
          email: null,
          phoneNumber: null,
          isGuest: false,
          // hasPlacedOrder: false,
        });
        ///setAuth({});
        setUserInfo(null);
        setActivitySettings(null);
      }
    } catch (error) {
      console.error("Session verification check error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔒 UPDATE: Modified to allow the backend to clear the secure cookie
  const logOut = async () => {
    try {
      setIsLoading(true);

      // Inform backend to clear out user sessions and wipe the secure cookie
      await axiosWithCredentials.post("/logOut");

      // Wipe application working states cleanly
      setAuth({});
      setUserInfo(null);
      setFirstName(null);
      setActivitySettings(null);
      setGoogleOnboardData(null);
      queryClient.clear();

      //console.log("Logged out successfully, cookie parameters cleared.");
    } catch (error) {
      console.error("Logout execution error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔒 UPDATE: Check session status exactly once when the PWA starts up
  useEffect(() => {
    isLoggedIn();
  }, []);

  return (
    <AppContext.Provider
      value={{
        auth,
        setAuth,
        logOut,
        isLoggedIn,
        isLoading,
        userToken, // Backwards compatible with legacy frontend routes expecting an access key check
        userInfo,
        firstname,
        activitySettings,
        isProfileComplete,
        hasUnacknowledgedRedemption,
        googleOnboardData, // Expose to IdentityScreen
        setGoogleOnboardData,
        setIsLoading,
        setUserInfo,
        setFirstName,
        setActivitySettings,
        isReverified,
        reverifyUser,
        updateInteraction,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
